import { useLayoutEffect, useState, type CSSProperties } from 'react';

const MOBILE_BREAKPOINT = 768;
const REFERENCE_CONTENT_WIDTH = 768;
const MIN_SCALE = 0.6;

/**
 * Small-viewport fallback shared by the Classic page root and the UI v2 shell:
 * at or below 768px the content is scaled down (to a floor of 0.6) so the page
 * fits beside a sidebar of `sidebarWidth` px. Returns the style for the
 * element that wraps the content, or undefined when no scaling applies.
 */
export function useContentScale(
  sidebarWidth: number
): CSSProperties | undefined {
  const [scale, setScale] = useState<number | null>(null);

  useLayoutEffect(() => {
    const updateScale = (): void => {
      if (window.innerWidth > MOBILE_BREAKPOINT) {
        setScale(null);
        return;
      }
      const contentWidth = window.innerWidth - sidebarWidth;
      setScale(
        Math.min(Math.max(contentWidth / REFERENCE_CONTENT_WIDTH, MIN_SCALE), 1)
      );
    };

    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, [sidebarWidth]);

  if (scale === null || scale >= 1) return undefined;
  return {
    transform: `scale(${scale})`,
    transformOrigin: 'top left',
    width: `${100 / scale}%`,
    height: 'auto',
  };
}
